/**
 * Company-specific validation utility functions
 * 
 * This file contains validation functions specifically designed for company creation and management forms.
 * All functions are prefixed with 'company' to clearly indicate their purpose.
 */

import { OrganizationType } from '../constants/companyOrganizationTypes';
import { isValidIndustryTypeId } from '../constants/companyIndustryTypes';

/**
 * COMPANY: Validates a company name
 * @param {string} companyName - The company name to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateCompanyName(companyName) {
  if (!companyName || companyName.trim() === '') {
    return {
      isValid: false,
      error: 'Company name is required'
    }
  }

  if (companyName.trim().length < 2) {
    return {
      isValid: false,
      error: 'Company name must be at least 2 characters'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a legal name
 * @param {string} legalName - The legal name to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateLegalName(legalName) {
  if (!legalName || legalName.trim() === '') {
    return {
      isValid: false,
      error: 'Legal name is required'
    }
  }

  if (legalName.trim().length < 2) {
    return {
      isValid: false,
      error: 'Legal name must be at least 2 characters'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a federal ID
 * @param {string} federalId - The federal ID to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateFederalId(federalId) {
  if (!federalId || federalId.trim() === '') {
    return {
      isValid: false,
      error: 'Federal ID is required'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a phone number
 * @param {string} phone - The phone number to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidatePhone(phone) {
  if (!phone || phone.trim() === '') {
    return {
      isValid: false,
      error: 'Phone number is required'
    }
  }

  // Basic phone validation (allows international format)
  const phoneRegex = /^[+]?[\d\s\-()]{7,20}$/
  if (!phoneRegex.test(phone.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates an email address format for company form
 * @param {string} email - The email string to validate (optional field)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateEmail(email) {
  // Email is optional, but if provided, must be valid
  if (!email || email.trim() === '') {
    return {
      isValid: true,
      error: null
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a website URL (required field)
 * @param {string} url - The URL string to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateWebsiteUrl(url) {
  // Website is required
  if (!url || url.trim() === '') {
    return {
      isValid: false,
      error: 'Website URL is required'
    }
  }

  try {
    const urlObj = new URL(url)
    
    // Ensure the URL uses http:// or https:// protocol
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return {
        isValid: false,
        error: 'Website URL must start with http:// or https://'
      }
    }

    // URL is valid
    return {
      isValid: true,
      error: null
    }
  } catch {
    // If URL constructor fails, try adding https:// prefix for common cases
    try {
      const urlWithProtocol = url.startsWith('www.') 
        ? 'https://' + url 
        : url.includes('://') 
          ? url 
          : 'https://' + url
      
      const urlObj = new URL(urlWithProtocol)
      
      // Check if the URL with added protocol is valid
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        // URL is valid but missing protocol - we'll accept it (can be auto-fixed)
        return {
          isValid: true,
          error: null
        }
      }

      // Protocol is not http or https
      return {
        isValid: false,
        error: 'Website URL must start with http:// or https://'
      }
    } catch {
      // URL cannot be made valid even with protocol prefix
      return {
        isValid: false,
        error: 'Please enter a valid website URL (e.g., https://example.com)'
      }
    }
  }
}

/**
 * COMPANY: Validates an address line
 * @param {string} address - The address to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateAddress(address) {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      error: 'Address is required'
    }
  }

  if (address.trim().length < 3) {
    return {
      isValid: false,
      error: 'Address must be at least 3 characters'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a city name
 * @param {string} city - The city to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateCity(city) {
  if (!city || city.trim() === '') {
    return {
      isValid: false,
      error: 'City is required'
    }
  }

  if (city.trim().length < 2) {
    return {
      isValid: false,
      error: 'City must be at least 2 characters'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a zip code
 * @param {string} zipCode - The zip code to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateZipCode(zipCode) {
  if (!zipCode || zipCode.trim() === '') {
    return {
      isValid: false,
      error: 'ZIP code is required'
    }
  }

  // Allow alphanumeric zip codes (for international formats)
  const zipRegex = /^[A-Z0-9\s-]{3,10}$/i
  if (!zipRegex.test(zipCode.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid ZIP code'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates encryption password with minimum length requirement
 * @param {string} password - The password to validate
 * @param {number} minLength - Minimum required length (default: 8)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateEncryptionPassword(password, minLength = 8) {
  if (!password || password.trim() === '') {
    return {
      isValid: false,
      error: 'Company password is required'
    }
  }

  if (password.length < minLength) {
    return {
      isValid: false,
      error: `Password must be at least ${minLength} characters`
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates that two encryption password fields match
 * @param {string} password - The original password
 * @param {string} confirmPassword - The password confirmation
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateEncryptionPasswordMatch(password, confirmPassword) {
  if (!confirmPassword || confirmPassword.trim() === '') {
    return {
      isValid: false,
      error: 'Please confirm your password'
    }
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'Passwords do not match'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates a required field for company form
 * @param {string} value - The value to validate
 * @param {string} fieldName - The name of the field (for error message)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateRequired(value, fieldName) {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: `${fieldName} is required`
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates industry selection
 * @param {string|number} industryId - The industry ID to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateIndustry(industryId) {
  if (!industryId || industryId === '' || industryId === null || industryId === undefined) {
    return {
      isValid: false,
      error: 'Industry type is required'
    }
  }

  if (!isValidIndustryTypeId(industryId)) {
    return {
      isValid: false,
      error: 'Please select a valid industry type'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * COMPANY: Validates legal organization type (US-style entity codes on Company.organizationType)
 * @param {string|number} organizationType - Integer code 0–16
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function companyValidateOrganizationType(organizationType) {
  if (organizationType === '' || organizationType === null || organizationType === undefined) {
    return {
      isValid: false,
      error: 'Organization type is required'
    }
  }
  const n = Number(organizationType)
  if (!Number.isInteger(n) || n < OrganizationType.COMPANY || n > OrganizationType.CLOSE_CORPORATION) {
    return {
      isValid: false,
      error: 'Please select a valid organization type'
    }
  }
  return {
    isValid: true,
    error: null
  }
}

